import type {
	ConstantsNotificationRuleChannelResponse,
	ConstantsNotificationRuleResponse,
} from "@gosaas/types";
import {
	isPredefinedNotificationChannel,
	PREDEFINED_NOTIFICATION_CHANNEL_LABELS,
} from "@gosaas/types/notification";
import { hasPermission, type Permission, type Role } from "@gosaas/types/rbac";
import { IconBell, IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
	CreateNotificationRuleDialog,
	formatEventLabel,
	usageThresholdPercentsFromConditions,
} from "@/components/dialogs/create-notification-rule";
import { DeleteNotificationRuleDialog } from "@/components/dialogs/delete-notification-rule";
import { Loader } from "@/components/Loader";
import { PageHeader } from "@/components/page-header";
import { PageLayout } from "@/components/page-layout";
import { useTeam } from "@/components/providers/team";
import { useFetchNotificationRules } from "@/components/queries/notifications";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { hasBillingFreeTier } from "@/lib/modules";
import { apiClient, requestWithHandling } from "@/utils/api";

/** Matches backend constants.DefaultSubscriptionNotificationRuleName. */
export const DEFAULT_SUBSCRIPTION_NOTIFICATION_RULE_NAME =
	"Subscription notifications";

export function isFreeNotificationPlan(planName: string | undefined): boolean {
	return hasBillingFreeTier() && planName === "Free";
}

export function canCreateNotificationRules(
	planName: string | undefined,
	canCreate: boolean,
): boolean {
	return canCreate && !isFreeNotificationPlan(planName);
}

export function canEditNotificationRule(
	planName: string | undefined,
	canUpdate: boolean,
): boolean {
	return canUpdate && !isFreeNotificationPlan(planName);
}

export function canDeleteNotificationRule(
	planName: string | undefined,
	canDelete: boolean,
): boolean {
	return canDelete && !isFreeNotificationPlan(planName);
}

export function canToggleNotificationRule(
	planName: string | undefined,
	canUpdate: boolean,
	ruleName: string | undefined,
): boolean {
	if (!canUpdate) return false;
	if (!isFreeNotificationPlan(planName)) return true;
	return ruleName === DEFAULT_SUBSCRIPTION_NOTIFICATION_RULE_NAME;
}

export function formatUsageThresholdBadge(conditions: unknown): string | null {
	const percents = usageThresholdPercentsFromConditions(conditions);
	if (percents.length === 0) return null;
	return percents.map((p) => `${p}%`).join(", ");
}

export function getChannelLabel(
	ch: ConstantsNotificationRuleChannelResponse,
): string {
	if (ch.predefined && isPredefinedNotificationChannel(ch.predefined)) {
		return PREDEFINED_NOTIFICATION_CHANNEL_LABELS[ch.predefined];
	}
	if (ch.predefined) return ch.predefined;
	return ch.channel_name ?? ch.channel_id ?? "Unknown";
}

export function DeleteRuleTargetDialog({
	deleteTarget,
	onClearTarget,
	onSuccess,
}: {
	deleteTarget: ConstantsNotificationRuleResponse | null;
	onClearTarget: () => void;
	onSuccess: () => void;
}) {
	if (!deleteTarget) {
		return null;
	}

	return (
		<DeleteNotificationRuleDialog
			open
			onOpenChange={(open) => {
				if (!open) onClearTarget();
			}}
			rule={deleteTarget}
			onSuccess={onSuccess}
		/>
	);
}

export function NotificationRules() {
	const { teamID, teams } = useTeam();
	const router = useRouter();
	const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
	const [editingRule, setEditingRule] =
		useState<ConstantsNotificationRuleResponse | null>(null);
	const [deleteTarget, setDeleteTarget] =
		useState<ConstantsNotificationRuleResponse | null>(null);
	const [togglingId, setTogglingId] = useState<string | null>(null);

	const { rules, isLoading, refetchRules } = useFetchNotificationRules(
		teamID ?? "",
	);

	const team = teams.find((t) => t.id === teamID);
	const canRead = hasPermission(
		team?.role as Role,
		"notifications:read" as Permission,
	);
	const canCreate = hasPermission(
		team?.role as Role,
		"notifications:create" as Permission,
	);
	const canUpdate = hasPermission(
		team?.role as Role,
		"notifications:update" as Permission,
	);
	const canDelete = hasPermission(
		team?.role as Role,
		"notifications:delete" as Permission,
	);
	const planName = team?.billing?.plan_name;
	const allowCreate = canCreateNotificationRules(planName, canCreate);
	const allowEdit = canEditNotificationRule(planName, canUpdate);
	const allowDelete = canDeleteNotificationRule(planName, canDelete);

	useEffect(() => {
		if (canRead === false) {
			toast.warning("You do not have permission to manage notifications", {
				description: "Please contact support if you believe this is an error.",
			});
			router.navigate({ to: "/403" });
		}
	}, [canRead, router]);

	async function handleToggle(rule: ConstantsNotificationRuleResponse) {
		if (!canUpdate) return;
		try {
			setTogglingId(rule.id as string);
			const response = await requestWithHandling<void>(() =>
				apiClient.api.teamNotificationsRulesUpdate(
					teamID as string,
					rule.id as string,
					{
						is_enabled: !rule.is_enabled,
					},
				),
			);
			if (response.code === "SUCCESS") {
				refetchRules();
			} else {
				toast.error("Failed to update rule", { description: response.message });
			}
		} catch {
			toast.error("Couldn't update this rule", {
				description: "Try again or reload the page.",
			});
		} finally {
			setTogglingId(null);
		}
	}

	if (!teamID) return <Loader />;
	if (isLoading) return <Loader />;
	if (!canRead) return null;

	return (
		<PageLayout>
			<PageHeader
				breadcrumbItems={[
					{ title: "Team", href: null },
					{ title: "Notifications", href: "/team/notifications" },
					{ title: "Notification rules", href: null },
				]}
				title="Notification rules"
				description={
					<>
						Define which events trigger notifications and where they're sent.
						{isFreeNotificationPlan(planName) ? (
							<>
								{" "}
								On the Free plan you can turn the default subscription rule on
								or off. Upgrade to create custom rules and alert channels.
							</>
						) : (
							<>
								{" "}
								Add{" "}
								<Link
									to="/team/notifications/channels"
									className="text-primary underline-offset-4 hover:underline"
								>
									alert channels
								</Link>{" "}
								first if you need new destinations.
							</>
						)}
					</>
				}
				icon={<IconBell className="size-6 text-primary" aria-hidden />}
				action={
					allowCreate && (
						<Button
							className="gap-2"
							onClick={() => {
								setEditingRule(null);
								setRuleDialogOpen(true);
							}}
						>
							<IconPlus className="size-4" />
							Create rule
						</Button>
					)
				}
			/>

			<div className="space-y-4">
				{!rules || rules.length === 0 ? (
					<p className="text-sm text-muted-foreground">
						No rules yet. Create one to start receiving notifications.
					</p>
				) : (
					<div className="rounded-lg border border-border/60">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Name</TableHead>
									<TableHead>Events</TableHead>
									<TableHead>Channels</TableHead>
									<TableHead>Enabled</TableHead>
									<TableHead className="text-right">Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{rules.map((rule) => (
									<TableRow key={rule.id}>
										<TableCell className="min-w-0 max-w-[22rem] whitespace-normal align-top font-medium">
											{rule.name}
											{rule.description && (
												<p className="mt-1 text-pretty text-sm text-muted-foreground">
													{rule.description}
												</p>
											)}
										</TableCell>
										<TableCell>
											<div className="flex max-w-[220px] flex-wrap gap-1">
												{(rule.event_types ?? []).map((et) => (
													<Badge key={et} variant="outline" className="text-xs">
														{formatEventLabel(et)}
													</Badge>
												))}
												{formatUsageThresholdBadge(rule.conditions) && (
													<Badge variant="secondary" className="text-xs">
														At {formatUsageThresholdBadge(rule.conditions)}{" "}
														usage
													</Badge>
												)}
											</div>
										</TableCell>
										<TableCell>
											<div className="flex flex-wrap gap-1">
												{rule.channels?.map(
													(
														ch: ConstantsNotificationRuleChannelResponse,
														i: number,
													) => (
														<Badge
															key={ch.id ?? i}
															variant="secondary"
															className="text-xs"
														>
															{getChannelLabel(ch)}
														</Badge>
													),
												)}
											</div>
										</TableCell>
										<TableCell>
											<Switch
												checked={rule.is_enabled}
												disabled={
													!canToggleNotificationRule(
														planName,
														canUpdate,
														rule.name,
													) || togglingId === rule.id
												}
												onCheckedChange={() => handleToggle(rule)}
											/>
										</TableCell>
										<TableCell className="text-right">
											<div className="flex justify-end gap-1">
												{allowEdit && (
													<Button
														variant="ghost"
														size="icon"
														onClick={() => {
															setEditingRule(rule);
															setRuleDialogOpen(true);
														}}
														aria-label={`Edit ${rule.name}`}
													>
														<IconPencil className="size-4" />
													</Button>
												)}
												{allowDelete && (
													<Button
														variant="ghost"
														size="icon"
														onClick={() => setDeleteTarget(rule)}
														aria-label={`Delete ${rule.name}`}
													>
														<IconTrash className="size-4" />
													</Button>
												)}
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				)}
			</div>

			<CreateNotificationRuleDialog
				open={ruleDialogOpen}
				onOpenChange={(open) => {
					setRuleDialogOpen(open);
					if (!open) setEditingRule(null);
				}}
				editingRule={editingRule}
				onSuccess={refetchRules}
			/>
			<DeleteRuleTargetDialog
				deleteTarget={deleteTarget}
				onClearTarget={() => setDeleteTarget(null)}
				onSuccess={refetchRules}
			/>
		</PageLayout>
	);
}

/* istanbul ignore start -- TanStack Router file route registration */
export const Route = createFileRoute("/_authorized/team/notifications/rules")({
	component: NotificationRules,
});
/* istanbul ignore end */
